import type { UserRole } from '@/contexts/AuthContext';

/**
 * Frontend permission matrices — mirror the backend `requireRole()` calls.
 *
 * **Source of truth is the backend.** When backend handlers change their
 * `requireRole()` lists, update the matching constant here. The frontend
 * `<RoleGate>` reads these to decide whether to render an action button.
 *
 * Naming convention: `CAN_<VERB>_<NOUN>`. Always export `as const` arrays
 * typed `readonly UserRole[]`.
 *
 * Backend handler reference is the comment next to each constant.
 */

/**
 * Feedback (2026-09-29, Blinded/unblinded item 2) — "admin should not be
 * seeing anything on eISF, visits, queries, subject". Confirmed to cover all
 * three admin roles, platform and application admins included.
 *
 * Mirrors services/access/clinicalAreas.ts on the backend, which is the
 * enforcing side: the server refuses regardless of what the UI shows. This
 * constant exists so the nav entries and routes disappear rather than
 * leading a user to a 403, and so no button is offered for an action that
 * cannot succeed.
 *
 * Applied as a subtraction over the CAN_* lists below (see
 * `withoutClinicalAdmins`) so each list keeps documenting its backend
 * handler as written, and the policy that overrides them stays in one place.
 */
export const CLINICAL_AREA_BLOCKED_ROLES: readonly UserRole[] = [
  'platform_admin',
  'application_admin',
  'site_admin',
  // Added 2026-09-30 — the client extended the rule to the study designer.
  // Site-tier only (`site_study_designer`), not the platform-tier
  // `study_designer`. It keeps forms, field templates, binder templates,
  // eISF master data, the study form and the dashboard; it loses subjects,
  // visits, queries and the eISF binder. Mirrors
  // DreamIT-Backend/src/services/access/clinicalAreas.ts, which enforces.
  'site_study_designer',
] as const;

export function isClinicalAreaBlocked(role: UserRole): boolean {
  return (CLINICAL_AREA_BLOCKED_ROLES as readonly string[]).includes(role);
}

/** Drops the three admin roles from a permission list. */
function withoutClinicalAdmins(roles: readonly UserRole[]): readonly UserRole[] {
  return roles.filter((r) => !isClinicalAreaBlocked(r));
}

// --- DREAM-7 ---

/** Source: handlers/sites/onboard.ts */
export const CAN_ONBOARD_SITE: readonly UserRole[] = [
  'platform_admin',
  'application_admin',
] as const;

/** Source: handlers/sites/invite.ts + invitationsList.ts + inviteResend.ts */
export const CAN_INVITE_TEAM: readonly UserRole[] = [
  'platform_admin',
  'application_admin',
  'site_admin',
] as const;

/** Source: handlers/admin/unlockUser.ts (platform path) */
export const CAN_UNLOCK_USERS: readonly UserRole[] = [
  'platform_admin',
  'application_admin',
  'site_admin', // site admins unlock their own site users
] as const;

// --- DREAM-11 — Compliance Hub ---

/** Source: handlers/compliance/auditTrail.ts (VIEW_AUDIT_ROLES) */
export const CAN_VIEW_AUDIT_TRAIL: readonly UserRole[] = [
  'platform_admin',
  'application_admin',
  'compliance',
  'monitor',
  'auditor',
  'site_admin',
  'principal_investigator',
  'sub_investigator',
  'coordinator',
] as const;

/** Source: handlers/compliance/accessLog.ts (VIEW_ACCESS_ROLES) */
export const CAN_VIEW_ACCESS_LOG: readonly UserRole[] = [
  'platform_admin',
  'application_admin',
  'compliance',
  'auditor',
  'site_admin',
] as const;

/** Source: handlers/compliance/queries.ts (MONITOR_ROLES) — external raisers. */
export const CAN_RAISE_QUERY: readonly UserRole[] = [
  'monitor',
  'auditor',
] as const;

/** Source: handlers/compliance/queries.ts (RESPOND_ROLES) */
const CAN_RESPOND_QUERY_RAW: readonly UserRole[] = [
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'site_admin',
  'regulatory_specialist',
  // A Site User acts with the role given on each study (Harish, 1 Oct 2026);
  // the server checks that role per study.
  'site_user',
] as const;
/** Admin roles removed — see CLINICAL_AREA_BLOCKED_ROLES. */
export const CAN_RESPOND_QUERY: readonly UserRole[] = withoutClinicalAdmins(CAN_RESPOND_QUERY_RAW);

/**
 * Punch-list f5 — internal staff (RESPOND_ROLES) can now raise queries too,
 * not just monitors/auditors (MONITOR_ROLES). Use this union wherever a
 * "Raise Query" affordance should be visible to either raiser type; use
 * `CAN_RAISE_QUERY` alone for surfaces that are specifically external
 * (e.g. a folder/document query, which stays monitor/auditor-only by
 * convention — see RaiseFolderQueryDialog).
 */
export const CAN_RAISE_QUERY_ANY: readonly UserRole[] = [
  ...CAN_RAISE_QUERY,
  ...CAN_RESPOND_QUERY,
] as const;

const CAN_VIEW_QUERIES_RAW: readonly UserRole[] = [
  'monitor',
  'auditor',
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'site_admin',
  'regulatory_specialist',
  'platform_admin',
  'application_admin',
  // compliance removed (Harish, 1 Oct 2026: compliance areas only).
  'site_user',
] as const;
/** Admin roles removed — see CLINICAL_AREA_BLOCKED_ROLES. */
export const CAN_VIEW_QUERIES: readonly UserRole[] = withoutClinicalAdmins(CAN_VIEW_QUERIES_RAW);

const CAN_REOPEN_QUERY_RAW: readonly UserRole[] = [
  'monitor',
  'auditor',
  'site_admin',
  'platform_admin',
  'application_admin',
] as const;
/** Admin roles removed — see CLINICAL_AREA_BLOCKED_ROLES. */
export const CAN_REOPEN_QUERY: readonly UserRole[] = withoutClinicalAdmins(CAN_REOPEN_QUERY_RAW);

// --- DREAM-14 — Subjects + PHI ---

/**
 * Source: handlers/subjects/list.ts + detail.ts — SUBJECT_ROLES held on the
 * study (services/access/subjectRoles.ts). Monitors/auditors reach completed
 * visits, not the subject list; Compliance sees no clinical data (1 Oct 2026).
 */
const CAN_VIEW_SUBJECTS_RAW: readonly UserRole[] = [
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'site_user',
] as const;
/** Admin roles removed — see CLINICAL_AREA_BLOCKED_ROLES. */
export const CAN_VIEW_SUBJECTS: readonly UserRole[] = withoutClinicalAdmins(CAN_VIEW_SUBJECTS_RAW);

/** Source: handlers/subjects/create.ts — PI/SI/Coordinator on the study. */
const CAN_CREATE_SUBJECT_RAW: readonly UserRole[] = [
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'site_user',
] as const;
/** Admin roles removed — see CLINICAL_AREA_BLOCKED_ROLES. */
export const CAN_CREATE_SUBJECT: readonly UserRole[] = withoutClinicalAdmins(CAN_CREATE_SUBJECT_RAW);

/** Source: handlers/phi/grant.ts + revoke.ts */
export const CAN_GRANT_PHI: readonly UserRole[] = [
  'platform_admin',
  'application_admin',
  'compliance',
  'site_admin',
  'principal_investigator',
] as const;

/** Source: handlers/phi/toggleRemoteView.ts — a monitor/auditor granted the study. */
export const CAN_TOGGLE_REMOTE_VIEW: readonly UserRole[] = [
  'monitor',
  'auditor',
] as const;

// --- DREAM-15 — CSR Export ---

/** Source: handlers/csr/create.ts — PI/SI with clinical access to the study. */
export const CAN_CREATE_CSR_EXPORT: readonly UserRole[] = [
  'principal_investigator',
  'sub_investigator',
] as const;

// --- DREAM-16 (CTMS core, Sprint 2) ---

/**
 * Study Portfolio (DREAM-17). The server lists only the caller's studies
 * (admin/set-up roles: the whole site). No Portfolio for Compliance; the
 * platform admins see it read-only (Harish, 1 Oct 2026, revised).
 */
export const CAN_VIEW_STUDY_PORTFOLIO: readonly UserRole[] = [
  // The platform admins view studies read-only (revised 1 Oct 2026).
  'platform_admin',
  'application_admin',
  'study_designer',
  'site_admin',
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'site_study_designer',
  'regulatory_specialist',
  'site_pharma',
  'finance_billing',
  'monitor',
  'auditor',
  'site_user',
] as const;

/**
 * Who can create studies + start the onboarding wizard (DREAM-17 + button,
 * DREAM-18). Source: handlers/studies/create.ts — PI, SI and Coordinator;
 * Site Admin and the platform admins no longer onboard studies (1 Oct 2026).
 */
export const CAN_CREATE_STUDY: readonly UserRole[] = [
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'site_study_designer',
  'site_admin',
] as const;

/** Who can step through onboarding + flip readiness flags + activate (DREAM-18). */
export const CAN_ONBOARD_STUDY: readonly UserRole[] = [
  'platform_admin',
  'application_admin',
  'site_admin',
  'site_study_designer',
  'coordinator',
] as const;

/**
 * Who can change per-study team / role assignments (DREAM-18 step 3, DREAM-20
 * team panel). Source: handlers/studies/staffAssign.ts — PI, SI and
 * Coordinator by the role held on the study; Site Admin only while the study
 * has no PI/SI/Coordinator yet (the server enforces both).
 */
export const CAN_MANAGE_STUDY_TEAM: readonly UserRole[] = [
  'site_admin',
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'site_user',
] as const;

/** Who can upload / replace the protocol document (DREAM-19). */
export const CAN_UPLOAD_PROTOCOL: readonly UserRole[] = [
  'platform_admin',
  'application_admin',
  'site_admin',
  'site_study_designer',
  'coordinator',
  'regulatory_specialist',
] as const;

/** Who can add / edit study links (DREAM-17 Study Links panel). */
export const CAN_MANAGE_STUDY_LINKS: readonly UserRole[] = [
  'site_admin',
  'study_designer',
  'site_study_designer',
] as const;

// --- DREAM39/DREAM40 — Budget & Finance ---

/**
 * Source: handlers/finance/_roles.ts (FINANCE_READ / FINANCE_WRITE). Finance is
 * for the admin roles and Finance/Billing only (Harish, 1 Oct 2026).
 */
export const CAN_VIEW_FINANCE: readonly UserRole[] = [
  'platform_admin',
  'application_admin',
  'site_admin',
  'finance_billing',
] as const;

export const CAN_GENERATE_INVOICE: readonly UserRole[] = [
  'platform_admin',
  'application_admin',
  'site_admin',
  'finance_billing',
] as const;

/** Source: handlers/onboarding/completeStep.ts + handlers/studies/schedule.ts */
export const CAN_SET_BILLABLE_RATE: readonly UserRole[] = [
  'platform_admin',
  'application_admin',
  'site_admin',
  'site_study_designer',
  'regulatory_specialist',
  'coordinator',
  'principal_investigator',
  'sub_investigator',
] as const;

// --- Page access (Phase 4, 1 Oct 2026) ---

/**
 * Which pages each sign-in role may open: the single list the sidebar and the
 * route guards both read, so a page is never offered that the server will
 * refuse, and never hidden when the server would allow it.
 *
 * Source of truth is still the backend (Harish's decisions of 1 Oct 2026,
 * applied in the per-study access and role-change commits). The server also
 * limits every page to the studies the user is on, with the role held ON each
 * study — so a Site User sees the study pages, which list only the studies it
 * has been added to (empty until then).
 */
export type PageKey =
  | 'dashboard'
  | 'studies'
  | 'studies.create'
  | 'studies.workspace'
  | 'studies.onboarding'
  | 'subjects'
  | 'visits'
  | 'queries'
  | 'forms'
  | 'esource'
  | 'binder'
  | 'doa'
  | 'approvals'
  | 'delegations'
  | 'search'
  | 'signature-queue'
  | 'document-library'
  | 'site-documentation'
  | 'staff-documentation'
  | 'team'
  | 'training'
  | 'compliance'
  | 'finance'
  | 'monitor-assignments'
  | 'platform-admin'
  | 'blinding'
  | 'regulatory-tags'
  | 'sponsors'
  | 'field-templates'
  | 'build-master-data'
  | 'rbac';

const PLATFORM_ADMINS: readonly UserRole[] = ['platform_admin', 'application_admin'];
const THREE_ADMINS: readonly UserRole[] = ['platform_admin', 'application_admin', 'site_admin'];
const PLATFORM_ADMIN_ONLY: readonly UserRole[] = ['platform_admin'];
const REVIEWERS: readonly UserRole[] = ['monitor', 'auditor'];
const STUDY_TEAM: readonly UserRole[] = ['principal_investigator', 'sub_investigator', 'coordinator'];

/** Roles allowed on each page (`'*'` = every signed-in role). */
const PAGE_ROLES: Record<PageKey, readonly UserRole[] | '*'> = {
  dashboard: '*',
  // Portfolio: no platform admins (no site/study content) or Compliance.
  studies: CAN_VIEW_STUDY_PORTFOLIO,
  'studies.create': CAN_CREATE_STUDY,
  // Workspace: also not Monitor/Auditor (they open the binder and completed visits).
  'studies.workspace': CAN_VIEW_STUDY_PORTFOLIO.filter((r) => !REVIEWERS.includes(r)),
  // Onboarding (Harish, 2 Oct 2026): PI, SI, Coordinator, Site Study Designer
  // and Site Admin, on any study at the site until it is activated.
  // Not CAN_CREATE_STUDY: creating a study and running an existing study's
  // onboarding are different acts, and the platform admins do the second but
  // not the first. This is only the fallback while /me/permissions is in
  // flight — `studies.onboarding` in the registry is what decides — but it
  // has to agree with it, or the wizard flashes a refusal before RBAC lands.
  'studies.onboarding': [...PLATFORM_ADMINS, 'site_admin', 'site_study_designer',
    'regulatory_specialist', 'principal_investigator', 'sub_investigator', 'coordinator'],
  subjects: CAN_VIEW_SUBJECTS,
  // Visits: the study team, Regulatory Specialist, and Monitor/Auditor (completed visits only).
  visits: [...STUDY_TEAM, 'regulatory_specialist', ...REVIEWERS, 'site_user'],
  queries: CAN_VIEW_QUERIES,
  // Form library: the designers (handlers/forms/list.ts refuses Compliance and Monitor/Auditor).
  // The study team (PI, SI, Coordinator) only attaches published forms to
  // its visits; building and publishing stays with the designers (2 Oct 2026).
  forms: ['platform_admin', 'application_admin', 'site_admin', 'site_study_designer', 'regulatory_specialist', ...STUDY_TEAM],
  esource: [...STUDY_TEAM, 'site_user'],
  // eISF binder: study staff on their approved studies; monitors and auditors
  // read their granted binder read-only with PHI documents redacted (Harish,
  // 1 Oct 2026). Admins and the Site Study Designer are barred (29/30 Sep);
  // Compliance has no study.
  binder: [...STUDY_TEAM, 'regulatory_specialist', 'site_pharma', 'finance_billing', 'study_designer', 'site_user',
    'clinical_nurse', 'clinical_lab_assistant', 'clinical_qe', 'external_lab', ...REVIEWERS],
  // DoA log and grid: PI, SI and Coordinator build it; Regulatory Specialist kept.
  doa: [...STUDY_TEAM, 'regulatory_specialist', 'site_user'],
  approvals: '*',
  // Delegations: the study team and the roles kept as they were; not the
  // admins, Site Study Designer, Compliance or Monitor/Auditor.
  delegations: [...STUDY_TEAM, 'regulatory_specialist', 'site_pharma', 'finance_billing', 'study_designer', 'site_user'],
  search: [...STUDY_TEAM, 'regulatory_specialist', 'site_pharma', 'finance_billing', 'study_designer', 'site_user'],
  'signature-queue': '*',
  'document-library': '*',
  // Site/staff documentation: everyone except the platform admins (no site content).
  'site-documentation': '*',
  'staff-documentation': '*',
  team: THREE_ADMINS,
  // Training: hidden for the platform admins and Compliance.
  training: '*',
  // Compliance Hub: anyone with at least one Hub tab (see the Hub page).
  compliance: [...THREE_ADMINS, 'compliance', ...REVIEWERS, ...STUDY_TEAM, 'regulatory_specialist'],
  finance: CAN_VIEW_FINANCE,
  // Monitor Assignments: decided by the RBAC grid (compliance.monitor_assignments,
  // PAGE_TO_OPERATION). This is only the fallback before /me/permissions
  // answers — the operation's shipped default (13 Oct 2026).
  'monitor-assignments': THREE_ADMINS,
  'platform-admin': PLATFORM_ADMINS,
  // Blinding, and all Master Data pages (eISF catalog, DoA responsibilities,
  // binder templates, regulatory tags, sponsors, field templates): Platform
  // Admin only (Harish, 1 Oct 2026). Site Admin / Site Study Designer still
  // read field templates inside the Form Designer, through the API.
  blinding: PLATFORM_ADMIN_ONLY,
  'regulatory-tags': PLATFORM_ADMIN_ONLY,
  sponsors: PLATFORM_ADMIN_ONLY,
  'field-templates': PLATFORM_ADMIN_ONLY,
  'build-master-data': PLATFORM_ADMIN_ONLY,
  // RBAC configuration screen — who may change the permission grid is itself
  // the most privileged action in the app, so Platform Admin only.
  rbac: PLATFORM_ADMIN_ONLY,
};

/**
 * Pages a role is explicitly kept off even where the page is open to all.
 *
 * `training` stays listed, and now AGREES with the backend instead of fighting
 * it. The same 1 Oct 2026 rule used to live here, in the backend's
 * assertTrainingAccess() and in listForAdmin's role sets; the backend copy has
 * been deleted and the training.* operation defaults carry it, which is what
 * the dashboard and nav actually consult.
 *
 * This entry is the FALLBACK, not the decision: canSeePage answers only while
 * /me/permissions is in flight and for modules not yet in the registry. It is
 * kept so that window shows the same answer RBAC is about to give, rather than
 * flashing a Training link that then disappears. If a site grants an admin
 * role training in the grid, RBAC wins the moment it loads.
 */
const PAGE_DENY: Partial<Record<PageKey, readonly UserRole[]>> = {
  'site-documentation': PLATFORM_ADMINS,
  'staff-documentation': PLATFORM_ADMINS,
  training: [...PLATFORM_ADMINS, 'compliance'],
};

/**
 * The study page (Workspace) read-only: the platform admins always; Site
 * Admin always (they onboard in the wizard); the Site Study Designer once the
 * study is activated (Harish, 2 Oct 2026). The server enforces the same
 * (services/access/onboardingAccess.ts).
 */
export function isStudyReadOnly(role: UserRole | null | undefined, activated = true): boolean {
  if (!role) return false;
  if (THREE_ADMINS.includes(role)) return true;
  return role === 'site_study_designer' && activated;
}

export function canSeePage(role: UserRole, page: PageKey): boolean {
  if (PAGE_DENY[page]?.includes(role)) return false;
  const roles = PAGE_ROLES[page];
  return roles === '*' || roles.includes(role);
}
